import AnimClrHandler from './anim-keyframe-handler/anim-clr-handler';
import { BehaviorType, PresetId, STTLTimeNodePresetClassType } from '@tencent/slide-sdk-model';
import SetHandler from './anim-keyframe-handler/set-handler';
import AnimHandler from './anim-keyframe-handler/anim-handler';
import AnimKeyframeHandlerContext from './anim-keyframe-handler';
import DefaultHandler from './anim-keyframe-handler/default-handler';
import { IAnimToStyleConverter, IParams } from './interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IConverterToStyle, IData } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { AnimCanvasProperty } from '../anim-constants';


export default class AnimToStyleConverter implements IAnimToStyleConverter {
    private static instance: IAnimToStyleConverter;
    public static getInstance(): IAnimToStyleConverter {
        if (!this.instance) {
            this.instance = new AnimToStyleConverter();
        }
        return this.instance;
    }

    /**
     * 获取单个动画节点的toStyle
     * @param 动画节点sdk数据
     * @returns 该动画的toStyle
     */
    public convertAnimToStyle<T extends IElementBaseModel>({
        unitTimeNode,
        duration,
        elementModel,
    }: IParams<T>): IConverterToStyle| undefined {
        let toStyle: IConverterToStyle  = {};
        const handlerContext = new AnimKeyframeHandlerContext();
        const handlerMap = {
            [BehaviorType.animClr]: new AnimClrHandler(),
            [BehaviorType.set]: new SetHandler(),
            [BehaviorType.anim]: new AnimHandler(),
        };
        const defaulrHandler = new DefaultHandler();
        const isFillColorAnim = unitTimeNode?.par?.cTn?.presetId === PresetId.FillColor
        && unitTimeNode?.par?.cTn?.presetClass === STTLTimeNodePresetClassType.emph;
        unitTimeNode.par?.cTn?.childTnLst?.egTimeNodeChoice?.forEach((choice) => {
            const [firstKey] = Object.keys(choice);
            const handler = handlerMap[firstKey] ?? defaulrHandler;
            handlerContext.setHandler(handler);
            const tempToStyle = handlerContext.executeHandler(choice[firstKey], duration, elementModel);
            if (!tempToStyle) return;
            const isNoFill = !elementModel?.props?.style?.fillBackground
                            || elementModel?.props?.style?.fillBackground?.noFill;
            if (isNoFill && !isFillColorAnim) {
                Reflect.deleteProperty(tempToStyle, AnimCanvasProperty.FILL_COLOR);
            }
            toStyle = this.mergeKeyframe(toStyle, tempToStyle);
        });

        const keyList = Object.keys(toStyle);
        if (!keyList.length) {
            window.log.error('convertAmimKeyframe error');
            return;
        }

        // 移除不符合预期的数据，比如
        //     { fontColor: [
        //         { value: 'rgba(255,255,255,1)', duration: 250, rate: 0 },
        //         { value: undefined, duration: 250, rate: 1 },
        //     ]},
        for (let i = keyList.length - 1; i >= 0; i--) {
            const key = keyList[i];
            const list = toStyle[key];
            const hasUndefinedValue = list.some(item => typeof item.value === 'undefined');
            if (hasUndefinedValue) {
                Reflect.deleteProperty(toStyle, key);
            }
        }

        return toStyle;
    }


    /**
     * 合并toStyle样式帧数据
     * @param toStyle IConverterToStyle
     * @param tempToStyle IConverterToStyle
     * @returns IConverterToStyle
     */
    private mergeKeyframe(toStyle: IConverterToStyle, tempToStyle: IConverterToStyle): IConverterToStyle {
        Object.entries(tempToStyle).forEach(([key, list]) => {
            const oldList = toStyle[key];
            if (!oldList) {
                toStyle[key] = list;
                return;
            }
            if (!Array.isArray(oldList)) return;
            const mergedList = this.mergeSortedList(oldList, tempToStyle[key]);
            toStyle[key] = mergedList;
        });

        return toStyle;
    }

    /**
     * 按rate比例顺序合并数组
     * @param a IData[]
     * @param b IData[]
     * @returns IData[]
     * a: [{value: 1, duration: 100, rate: 0.2}, {value: 2, duration: 100, rate: 0.8}]
     * b: [{value: 3, duration: 100, rate: 0.1}, {value: 4, duration: 100, rate: 0.9}]
     * 合并后结果为:
     *  [
     * {value: 3, duration: 100, rate: 0.1},
     * {value: 1, duration: 100, rate: 0.2},
     * {value: 2, duration: 100, rate: 0.8},
     * {value: 4, duration: 100, rate: 0.9}]
     */
    private mergeSortedList(a: IData[], b: IData[]): IData[] {
        const mergedList: any[] = [] ;
        let i = 0;
        let j = 0;

        while (i < a.length && j < b.length) {
            if (a[i].rate < b[j].rate) {
                mergedList.push(a[i]);
                i = i + 1;
            } else {
                mergedList.push(b[j]);
                j = j + 1;
            }
        }

        while (i < a.length) {
            mergedList.push(a[i]);
            i = i + 1;
        }

        while (j < b.length) {
            j = j + 1;
        }

        return mergedList;
    }
}


export function getAnimToStyleConverter(): IAnimToStyleConverter {
    return AnimToStyleConverter.getInstance();
}
